import { INVITE_JOIN_MSG, IP_GUARD_MSG, MSG, PASSWORD_MSG, VIEW_MSG, passwordChecks, type Product } from "@genbaryoku/contract";
import { Children, cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { UseFormRegisterReturn } from "react-hook-form";
import { Link } from "react-router";
import { productInfo } from "../lib/product.js";
import { useProduct } from "../lib/useProduct.js";
import { TextSize } from "./TextSize.jsx";
import { ViewSwitch } from "./ViewSwitch.jsx";
import { dismissNotice, notify, type NoticeAction, type NoticeTone } from "./Notice.jsx";
import { NoticeBand, useNotices } from "./NoticeBand.jsx";

/**
 * 下の帯（2026-09-18）。band＝ログイン前（淡い地色・運営あり）／なし＝ログイン後の細いフッター（全ページ）。
 * ログイン前（#115・#120、docs/44 §3・§8.4）：
 *   PC版は2段＝上の段 左にリンク・右に「文字の大きさ［小｜中｜大］［PC・スマホの切替のアイコン（出す条件のときだけ）］」、
 *   下の段 左に「運営」「©」・右に「IP Geolocation by DB-IP」。
 *   スマホ版は3段＝リンク（先頭に「トップページ」。上の帯が無いので）→ 文字の大きさ［小｜中｜大］［アイコン］→「©」「IP Geolocation by DB-IP」（運営は出さない）。形は CSS（html[data-view="phone"]）
 */
export function SiteFooter({ band = false }: { band?: boolean }) {
  const sizeId = useId();
  const links = (
    <>
      <Link to="/terms">{MSG.footerTerms}</Link>
      <Link to="/privacy">{MSG.footerPrivacy}</Link>
      <Link to="/legal">{MSG.footerTokushoho}</Link>
      {/* お問い合わせはフォーム（#124 の 3a）。メールの窓口はフォームの下に書く */}
      <Link to="/contact">{MSG.footerContact}</Link>
    </>
  );
  if (!band) {
    return (
      <footer className="sitefoot slim">
        <nav aria-label="規約など">{links}</nav>
        <span className="sp" />
        <span className="copy">{MSG.footerCopyright}</span>
      </footer>
    );
  }
  return (
    <footer className="sitefoot band">
      <div className="frow">
        <nav aria-label="規約など">
          <a className="ftop" href="/">{VIEW_MSG.topPage}</a>
          {links}
        </nav>
        <span className="fsize">
          <span id={sizeId}>{VIEW_MSG.textSize}</span>
          <TextSize labelledBy={sizeId} />
          <ViewSwitch />
        </span>
      </div>
      <div className="frow">
        <span className="oper">{MSG.footerOperator}</span>
        <span className="copy">{MSG.footerCopyright}</span>
        {/* 国の判定（#110、docs/42）は DB-IP の IP to Country Lite（CC BY 4.0）。ログイン前の画面（ログイン・会員登録）に出典のリンク */}
        <a className="attrib" href={IP_GUARD_MSG.attributionUrl} target="_blank" rel="noopener noreferrer">
          {IP_GUARD_MSG.attribution}
        </a>
      </div>
    </footer>
  );
}

/**
 * ログイン前の枠のロゴ（ローマ字つき・下の二重罫線、docs/10 §4.3）。phoneOnly＝スマホ版だけ出す
 * （会社を選ぶ・招待の画面は PC版では上の帯にロゴがあるが、スマホ版は上の帯を出さないので枠の頭に置く）
 */
export function AuthLogo({ phoneOnly = false, product }: { phoneOnly?: boolean; product?: Product }) {
  // product を渡すとその製品のロゴ（/handoff＝このドメインの製品。ME の製品ではない。#194）。渡さなければ今の製品
  const cur = useProduct();
  const P = product ? productInfo(product) : cur;
  return (
    <div className={"authlogo" + (phoneOnly ? " phoneonly" : "")}>
      {/* 高さも書く（#179：無いとロゴが届くまで高さ 0 で、届いた瞬間に下の欄・ボタンが約 80px ずれる。ロゴは両製品とも 横:縦＝約 3.2:1） */}
      <img src={P.assets.logo} alt={P.name} width={264} height={82} />
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 12l5 5 11-11" />
    </svg>
  );
}

/** 見積書の小さな紙の見本（製品ごと。049） */
const MINI_SHEET: Record<Product, { to: string; rows: [string, string][]; total: string }> = {
  genba: { to: "山田建設 株式会社 御中", rows: [["足場（架払い共）", "216,000"], ["高圧洗浄", "54,000"], ["外壁塗装（シリコン3回塗り）", "504,000"]], total: "935,000円" },
  eigyo: { to: "株式会社 山田商会 御中", rows: [["業務用プリンター A3対応", "298,000"], ["初期設定・搬入作業", "36,000"], ["保守サポート（12か月）", "72,000"]], total: "446,600円" },
};

/** ログインの右側：この製品の一言と安心の3点、見積書の小さな紙 */
function LoginAside() {
  const P = useProduct();
  const sheet = MINI_SHEET[P.product];
  return (
    <aside className="authside">
      <div className="kicker">{P.copy.audience}</div>
      <p className="lead">
        {P.copy.lead[0]}
        <br />
        {P.copy.lead[1]}
      </p>
      <ul className="points">
        <li>
          <CheckIcon />
          初期費用 0円。メールアドレスだけで登録
        </li>
        <li>
          <CheckIcon />
          インボイス制度・電子帳簿保存法に対応
        </li>
        <li>
          <CheckIcon />
          品目の登録は不要。入力した内容が次回の候補に
        </li>
      </ul>
      <div className="minisheet" aria-hidden="true">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span className="ttl">見積書</span>
          <span style={{ fontSize: 12, color: "var(--ink2)" }}>Q-26-0012</span>
        </div>
        <div style={{ fontSize: 13, borderBottom: "3px double var(--ink)", paddingBottom: 6 }}>{sheet.to}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--ink2)" }}>
          {sheet.rows.map(([name, amount]) => (
            <div key={name} style={{ display: "flex", justifyContent: "space-between" }}><span>{name}</span><span>{amount}</span></div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--bold)", paddingTop: 6, fontSize: 14, fontWeight: 700 }}>
          <span>合計（税込）</span>
          <span>{sheet.total}</span>
        </div>
      </div>
    </aside>
  );
}

/** 会員登録の右側：「はじめかた」3歩 */
function SignupAside() {
  const steps: [string, string][] = [
    ["メールとパスワードを登録", "クレジットカードは要りません。"],
    ["届いたメールのリンクを押す", "それでログインできます。"],
    ["見積を1枚つくる", "お客様の名前と件名、明細を数行。PDFがすぐ出ます。"],
  ];
  return (
    <aside className="authside">
      <div>
        <div className="kicker">はじめかた</div>
        <p className="lead" style={{ fontSize: "var(--fs-30)" }}>登録は1分。初期設定はありません。</p>
      </div>
      <ol className="steps">
        {steps.map(([t, s], i) => (
          <li key={t}>
            <span className="no">{i + 1}</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <b style={{ fontSize: "var(--fs-17)" }}>{t}</b>
              <span className="hint">{s}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className="foot">国内サーバーで管理し、重要な個人情報は暗号化して保存します。</div>
    </aside>
  );
}

/**
 * ログイン前の画面の枠（2026-09-18）：上の帯（ロゴ＝/ へ、トップページへ、会員登録／ログイン）＋
 * 左の入力の枠＋右の紹介（900px 未満は隠す）＋下の帯。
 * mode="signup" は右が「はじめかた」、帯の右が「ログイン」になる。
 * スマホ版（html[data-view="phone"]、#115、docs/10 §9.14）は上の帯と右の紹介を出さず、白い紙1枚（上 28・左右 16・下 32、部品の間 16、最大 480px で中央）。形は CSS だけで変える
 */
export function AuthCard({
  title,
  children,
  onSubmit,
  mode = "login",
  help,
  plain = false,
  notices = false,
}: {
  title: string;
  children: React.ReactNode;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  mode?: "login" | "signup";
  /** 見出しの右の「？」（#92） */
  help?: ReactNode;
  /** 紹介と「会員登録／ログイン」を出さず、枠を広く（返事のリンクのページ、#124 の 3a） */
  plain?: boolean;
  /** 運営の案内の帯を枠の上に出す（docs/95 ★2：ログイン・会員登録・パスワード再設定）。届くまで（1.5 秒まで）枠を描かない（#179） */
  notices?: boolean;
}) {
  const P = useProduct();
  const n = useNotices(notices);
  const body = (
    <>
      {/* 製品の顔＝横組みのロゴ（ローマ字つき、幅 264）＋下の二重罫線（docs/10 §4.3。10/2 射場さん「ローマ字が入っていない」で戻した。上の帯はローマ字なしのまま） */}
      <AuthLogo />
      <h1>
        {title}
        {help}
      </h1>
      {children}
    </>
  );
  return (
    <div className="authpage" data-product={P.product}>
      <header className="authtop">
        <a href="/" className="brand">
          <img src={P.assets.logoHeader} alt={P.name} width={320} height={100} />
        </a>
        <span className="sp" />
        <a className="toplink" href="/">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          トップページへ
        </a>
        {plain ? null : mode === "signup" ? (
          <Link className="btn quiet" to="/login">ログイン</Link>
        ) : (
          <Link className="btn" to="/signup">会員登録（無料）</Link>
        )}
      </header>
      <NoticeBand items={n.items} />
      {n.ready ? (
        <main className={"auth" + (plain ? " plain" : "")}>
          {onSubmit ? (
            <form className="authcard" onSubmit={onSubmit} noValidate>
              {body}
            </form>
          ) : (
            <div className="authcard">{body}</div>
          )}
          {plain ? null : mode === "signup" ? <SignupAside /> : <LoginAside />}
        </main>
      ) : (
        <main className={"auth" + (plain ? " plain" : "")} aria-busy="true" />
      )}
      <SiteFooter band />
    </div>
  );
}

/** 「‹ ログインに戻る」（ログイン前の画面の文字のボタン） */
export function BackToLogin() {
  return (
    <Link className="btn text back" to="/login">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      {MSG.backToLogin}
    </Link>
  );
}

/**
 * 入力の欄（2026-09-29 射場さんの決定 B＝Material の「浮く名前」、docs/10 §4.4）。
 *   空の欄は名前が中（17px 薄墨）、入力中か値があると上に浮いて 14px・藍・太字。欄の高さは 52px。
 *   DOM は 欄 → 名前 の順（CSS の兄弟セレクタで浮かせる）。label の htmlFor はそのまま＝getByLabel は今までどおり効く。
 *   欄（input・textarea）に placeholder が無ければ " "（空白1つ）を足す：:placeholder-shown で「空」を見分けるため。「例：…」は置かない。
 *   select・type=date は名前を常に浮かせる（CSS）。help は欄の中の右端に「？」を置く（名前に付けると浮く名前と重なる）
 *   end＝欄の中の右端に置く操作（2026-10-14 射場さん「案A」、docs/10 §4.4、Figma「会社情報取得（10/14）」①）。
 *     欄と同じ高さ 52px・左に細い仕切り線（28px）。中身は FieldAction（絵＋藍の太字）。欄の文字と浮く名前は操作の幅だけ右を空ける
 *     （幅は文字の大きさで変わるので、描く前に測って --fend-w に入れる）
 */
export function Field({
  label,
  hint,
  children,
  id,
  help,
  end,
  style,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  id: string;
  help?: ReactNode;
  end?: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  const kids = Children.map(children, (c) => {
    if (!isValidElement<{ placeholder?: string; type?: string }>(c)) return c;
    const isText = c.type === "textarea" || (c.type === "input" && !["checkbox", "radio", "date", "file", "hidden"].includes(c.props.type ?? "text"));
    return isText && c.props.placeholder == null ? cloneElement(c, { placeholder: " " }) : c;
  });
  const endRef = useRef<HTMLSpanElement>(null);
  const [endW, setEndW] = useState(0);
  const hasEnd = !!end;
  useLayoutEffect(() => {
    const el = endRef.current;
    if (!hasEnd || !el) return;
    const measure = () => setEndW(el.offsetWidth);
    measure();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [hasEnd]);
  return (
    <div className={"field" + (className ? ` ${className}` : "")} style={hasEnd && endW ? ({ ...style, "--fend-w": `${endW}px` } as CSSProperties) : style}>
      {kids}
      <label htmlFor={id}>{label}</label>
      {help ? <span className="fhelp">{help}</span> : null}
      {end ? (
        <span className="fend" ref={endRef}>
          {end}
        </span>
      ) : null}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

/**
 * 欄の中の右端の操作（Field の end）：絵（20px）＋藍の太字。押せる範囲は欄と同じ高さ 52px・幅 44px 以上。
 * narrowIcon＝狭いとき（スマホ版・幅 767px 以下＝useNarrow と同じ境目）は絵だけの 44px 角（仕切り線は残す）。
 *   文字は見えなくするだけで読み上げの名前に残る（title もそのまま）。切り替えは CSS だけ（描いたあとにずれない）
 */
export function FieldAction({ icon, children, onClick, title, narrowIcon = false, testId }: { icon: ReactNode; children: ReactNode; onClick: () => void; title?: string; narrowIcon?: boolean; testId?: string }) {
  return (
    <button type="button" className={"fend-act" + (narrowIcon ? " is-narrow-icon" : "")} onClick={onClick} title={title} data-testid={testId}>
      {icon}
      <span className="fend-tx">{children}</span>
    </button>
  );
}

/**
 * スイッチ（9/29、docs/10 §4.4）：すぐ効く設定。<input type="checkbox" role="switch"> は本物のまま（つまみの枠に重ねて透明）。
 *   枠 52×32・つまみ 26px・200ms ですべる・オンは藍。読み上げは label と aria-checked。押せる範囲は 44px（.sw の min-height）
 *   2026-10-21 射場さん（docs/89 ★15・docs/10 §4.4）：**状態の文字（オン／オフ・使う／使わない）を出さない**＝つまみ＋名前だけ（状態はつまみの位置と色の両方で分かる）。
 *   名前は children（見出しの字の大きさなど自分で決めるとき）、無ければ label を名前として出す。名前も押せる（全体が <label>）。
 *   bare＝名前を出さない（名前がスイッチの左・上にもうある所＝設定の行・見出しの下など）
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  testId,
  children,
  bare = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  /** 読み上げの名前（getByRole("switch", { name }) もこれ）。children が無く bare でなければ、つまみの右にこの名前を出す */
  label: string;
  disabled?: boolean;
  testId?: string;
  children?: ReactNode;
  bare?: boolean;
}) {
  return (
    <label className="sw">
      <input type="checkbox" role="switch" aria-checked={checked} aria-label={label} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span className="track" aria-hidden="true">
        <span className="thumb" />
      </span>
      {children ?? (bare ? null : <span className="swn" aria-hidden="true">{label}</span>)}
    </label>
  );
}

/**
 * パスワード欄＋右の「表示／隠す」の字（#186、2026-10-15 射場さん：参加の画面（#185）と同じ字に、ログイン・会員登録・再設定・変更もそろえた。
 *   前は 2026-09-22 からの目のアイコン。アイコンより字のほうが分かる人に合わせる）
 *   - 押すと文字が見える（打ち間違いを目で確かめられる。docs/10 §9.2）。読み上げは「パスワードを表示／隠す」
 *   - RHF の register（reg）か、value/onChange のどちらでも使える
 *   - Mac／iPhone／Windows／Android の「強いパスワードを作る」が効くように、autoComplete は必ず new-password／current-password。
 *     同じ form の中に autoComplete="username" のメールアドレスの欄を置く（無いとユーザー名が空で保存される）
 *   - 「？」は欄の右に置かない。見出し・窓の題に置く（欄の右に置くとその欄だけ狭くなり、上の欄と幅がずれる。S-00005）
 */
export function PasswordInput({
  id,
  reg,
  autoComplete,
  value,
  onChange,
  describedBy,
}: {
  id: string;
  reg?: UseFormRegisterReturn;
  autoComplete: "current-password" | "new-password";
  value?: string;
  onChange?: (v: string) => void;
  /** 下の ✓ の箇条書きなど（aria-describedby） */
  describedBy?: string | undefined;
}) {
  const [show, setShow] = useState(false);
  const bound = reg ?? { value: value ?? "", onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value) };
  return (
    <div className="pw is-textbtn">
      <input id={id} className="inp" type={show ? "text" : "password"} autoComplete={autoComplete} spellCheck={false} autoCapitalize="off" placeholder=" " aria-describedby={describedBy} {...bound} />
      <button type="button" className="eye is-text" aria-label={show ? "パスワードを隠す" : "パスワードを表示"} aria-pressed={show} onClick={() => setShow((s) => !s)} data-testid="pw-toggle">
        {show ? INVITE_JOIN_MSG.hide : INVITE_JOIN_MSG.show}
      </button>
    </div>
  );
}

/**
 * パスワードの条件の ✓ の箇条書き（#185、docs/74 §3 ②・§4。2026-10-14 射場さん：緑の強さのバーはやめ、条件を ✓ で）。
 *   ① 8文字以上（いま n文字） ② 英字と数字を両方含む ③ 推測されにくい。物差しは contract の passwordChecks（API の 400 と同じ）。
 *   満たした行は藍の ✓、まだの行は薄墨の ○、使えない行（よくあるパスワードなど）は朱の ✕ と理由（色だけで伝えない＝記号と読み上げの字）。
 *   参加・会員登録・再設定・変更・設定（パスワードを設定）で同じもの
 */
export function PasswordRules({ value, email, id }: { value: string; email?: string | null | undefined; id?: string }) {
  const checks = passwordChecks(value, { email });
  return (
    <ul className="pwrules" id={id} aria-live="polite" data-testid="pw-rules">
      {checks.map((c) => (
        <li key={c.key} className={`pwrule is-${c.state}`} data-state={c.state} data-testid={`pw-rule-${c.key}`}>
          <span className="pwrule-mark" aria-hidden="true">
            {c.state === "ok" ? "✓" : c.state === "ng" ? "✕" : "○"}
          </span>
          <span className="pwrule-text">
            {c.label}
            <span className="vh">{c.state === "ok" ? "（満たしています）" : c.state === "ng" ? "（使えません）" : "（まだです）"}</span>
            {c.reason ? <span className="pwrule-why">{c.reason}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** パスワード管理（Safari・Chrome・Edge）に「このアカウントのパスワード」と分からせるための、見えないメールアドレスの欄（画面にメールを出さない form で） */
export function HiddenUsername({ email }: { email: string }) {
  return <input type="email" name="email" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" className="vh" />;
}

/**
 * 「？」の説明（2026-09-22 射場さん：説明の文は画面に並べず、重要なものを除いて「？」アイコンにまとめる）
 *   押すと下に小さな吹き出しで説明が出る。もう一度押す・外を押す・Esc で閉じる。読み上げは「説明」＋aria-expanded
 *   右端に近いときは吹き出しを右寄せにして画面からはみ出さない。
 *   9/29（docs/10 §5.1.1）：線だけの 20px のアイコン・薄墨・押す範囲 28px（枠と太字をやめた）。開くと淡い藍の地・藍。吹き出しは黒地に白字
 *   10/5（#138）：吹き出しは document.body の直下に出す（portal）。表の行・枠（overflow）・重なりの順に隠れない。
 *   置く場所はボタンの位置から（position: fixed）：下に出す・右端に近ければ右揃え（is-right）・下が足りず上の方が広ければ上に（is-up）。
 *   入らない分は吹き出しの中でスクロール。画面が動く（吹き出しの中のスクロールは除く）・大きさが変わると閉じる
 */
type HelpPos = { top?: number; bottom?: number; left?: number; right?: number; maxHeight: number; up: boolean; alignRight: boolean };
export function Help({ text, label = "説明", testId }: { text: React.ReactNode; label?: string; testId?: string }) {
  const [pos, setPos] = useState<HelpPos | null>(null);
  const open = !!pos;
  const id = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  /** 開いた時刻。押した直後のスクロールの知らせ（フォーカスで画面が少し動く）では閉じない（RowMenu と同じ） */
  const openedAt = useRef(0);
  const place = (): HelpPos | null => {
    const b = btn.current?.getBoundingClientRect();
    if (!b) return null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const alignRight = vw - b.left < 340;
    const below = vh - b.bottom - 8 - 8;
    const above = b.top - 8 - 8;
    const h = pop.current?.scrollHeight ?? 0;
    const up = h > below && above > below;
    // 吹き出しの幅（styles.css の .helppop と同じ）。狭い画面で真ん中あたりの「？」でも、左右 8px の内に収める
    const w = Math.min(320, vw - 32);
    return {
      ...(up ? { bottom: vh - b.top + 8 } : { top: b.bottom + 8 }),
      ...(alignRight ? { right: Math.max(8, Math.min(vw - b.right, vw - w - 8)) } : { left: Math.max(8, Math.min(b.left, vw - w - 8)) }),
      maxHeight: Math.max(80, up ? above : below),
      up,
      alignRight,
    };
  };
  // 描いてから吹き出しの高さを測り、下に入らなければ上に置き直す
  useLayoutEffect(() => {
    if (!pos || !pop.current) return;
    const next = place();
    if (next && (next.up !== pos.up || next.maxHeight !== pos.maxHeight)) setPos(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) => !!t && ((btn.current?.contains(t as Node) ?? false) || (pop.current?.contains(t as Node) ?? false));
    const onDoc = (e: MouseEvent) => { if (!inside(e.target)) setPos(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPos(null); };
    const onMove = (e: Event) => {
      if (e.type === "scroll" && pop.current?.contains(e.target as Node)) return; // 吹き出しの中のスクロール
      if (performance.now() - openedAt.current < 250) return;
      setPos(null);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);
  const toggle = () => {
    if (open) return setPos(null);
    openedAt.current = performance.now();
    setPos(place());
  };
  const cls = ["helppop", pos?.alignRight ? "is-right" : "", pos?.up ? "is-up" : ""].filter(Boolean).join(" ");
  return (
    <span className="help">
      {/* 開いているときの Esc は吹き出しだけを閉じる（窓の中の「？」で窓ごと閉じない＝「会社情報取得」の出典） */}
      <button type="button" ref={btn} className="helpbtn" aria-label={label} aria-expanded={open} aria-controls={id} onClick={toggle} data-testid={testId} onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); setPos(null); } }}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7M12 17h.01" />
        </svg>
      </button>
      {pos
        ? createPortal(
            <span role="tooltip" id={id} ref={pop} className={cls} style={{ top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right, maxHeight: pos.maxHeight }}>
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

/** 間違いは色でなく文で（§5.1）。空なら出ない */
export function ErrorText({ children }: { children?: string | undefined }) {
  return (
    <p className="err" role="alert" aria-live="assertive">
      {children ? `⚠ ${children}` : ""}
    </p>
  );
}

export type ToastAction = NoticeAction;
/** useToast の toast の形（部品に渡すときの型） */
export type ToastFn = (text: string | null, action?: ToastAction, tone?: NoticeTone, testId?: string) => void;
/**
 * 操作の結果の知らせ（#168 から通知 components/Notice.tsx の1つの置き場に出す。右下・15秒・× と Esc・3つまで）。
 * toast(文) で成功、toast(文, ボタン) で「元に戻す」など、toast(文, undefined, "error") で誤り（消えない）。toast(null) でこの画面の前の知らせ（誤りでないもの）を消す。
 * 同じ画面（この hook）の次の知らせは、前の知らせ（誤りでないもの）を置き換える＝続けて押しても積み上がらない。誤りは × か Esc まで残す。
 * node は前の形の名残（描くのは App の NoticeHost）＝いつも null
 */
export function useToast() {
  const last = useRef<{ id: number; tone: NoticeTone } | undefined>(undefined);
  const toast: ToastFn = useCallback((text, action, tone = "success", testId) => {
    if (last.current && last.current.tone !== "error") dismissNotice(last.current.id);
    last.current = text ? { id: notify({ text, action, tone, testId }), tone } : undefined;
  }, []);
  return { toast, node: null as ReactNode };
}

/** 変換確定の Enter で送らない（docs/10 §5.2 と同じ） */
export function guardComposingEnter(e: React.KeyboardEvent<HTMLElement>) {
  if (e.key === "Enter" && (e.nativeEvent.isComposing || e.keyCode === 229)) e.preventDefault();
}

export function useFieldIds<const K extends readonly string[]>(keys: K): Record<K[number], string> {
  const base = useId();
  const out = {} as Record<K[number], string>;
  for (const k of keys) out[k as K[number]] = `${base}-${k}`;
  return out;
}
